import { useState, useRef, useEffect, useCallback, useMemo } from "react";
import PropTypes from 'prop-types';
import { Dropdown } from 'react-bootstrap';
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faPen,
  faTrash,
  faThumbsUp,
  faComment,
  faHeart,
  faShare,
  faDonate,
  faFlag,
  faLockOpen,
  faGem
} from '@fortawesome/free-solid-svg-icons';
import {
  faThumbsUp as faThumbsUpRegular,
  faHeart as faHeartRegular
} from '@fortawesome/free-regular-svg-icons';
import { useTranslation } from 'react-i18next';

import EllipsisToggle from '@/components/ui/EllipsisToggle';
import MessageModal from '@/components/ui/MessageModal';
import StatusModal from '@/components/ui/StatusModal';
import PostUnlockModal from './PostUnlockModal';
import PostSettingBadges from './PostSettingBadges';
import SubscriptionPlanModal from '@/features/subscription/components/SubscriptionPlanModal';
import SubscriptionConfirmModal from '@/features/subscription/components/SubscriptionConfirmModal';
import PostComments from './PostComments';
import PostImages from './PostImages';
import PostEditModal from './PostEditModal';
import DonateModal from '@/components/ui/DonateModal';
import ReportModal from '@/components/ui/ReportModal';
import ClampText from '@/components/shared/ClampText';
import Axios from '@/shared/api/axios';
import { fetchContentItem } from '@/shared/api/contentItem';
import { parseBackendDate } from '@/shared/utils/parseBackendDate';
import { isImeComposingKey } from '@/shared/utils/imeKeyboard';
import { useStreamPolling, extractPostUnreadyVideos } from '@/hooks/useStreamPolling';
import { useUnmountSafeTimeout } from '@/hooks/useUnmountSafeTimeout';

import placeholder_portrait_40x40 from "@/assets/imgs/placeholders/portrait_40x40.png";
import IdolAvatar from '@/components/ui/IdolAvatar';
import IdolName from '@/components/ui/IdolName';
import RelativeTime from '@/components/shared/RelativeTime';

// 常數定義
const COMMENTS_PER_PAGE = 5;

// 在留言陣列（含巢狀 comment_sub_comments）中尋找並更新/移除單筆留言
// updater 回傳 null/undefined 代表移除該筆留言
// 回傳 { list, changed }
function updateCommentTree(comments, targetId, updater) {
  let changed = false;
  const list = (comments || []).reduce((acc, comment) => {
    if (comment.comment_id === targetId) {
      changed = true;
      const updated = updater(comment);
      if (updated) acc.push(updated);
      return acc;
    }
    if (comment.comment_sub_comments && comment.comment_sub_comments.length) {
      const sub = updateCommentTree(comment.comment_sub_comments, targetId, updater);
      if (sub.changed) {
        changed = true;
        acc.push({ ...comment, comment_sub_comments: sub.list });
        return acc;
      }
    }
    acc.push(comment);
    return acc;
  }, []);
  return { list, changed };
}

const PostCard = (props) => {
  const navigate = useNavigate();
  // 刪除世界觀後延遲導頁用：期間若已離開頁面，卸載時清掉 timer，不會被拉回來
  const setRedirectTimeout = useUnmountSafeTimeout();
  const { t } = useTranslation();

  // Modal refs
  const modalDelete = useRef(null);
  const modalDeleteComment = useRef(null);
  const modalShare = useRef(null);
  const modalShareFailed = useRef(null);
  const modalCommentSent = useRef(null);
  const modalPostDelSuccess = useRef(null);
  const modalCommentSuccess = useRef(null);
  const modalCommentDelSuccess = useRef(null);
  const modalUnlockSuccess = useRef(null);
  const donateModalRef = useRef(null);
  const modalDonateSuccess = useRef(null);
  const modalSubscribeSuccess = useRef(null);
  const statusModalRef = useRef(null);
  const reportModalRef = useRef(null);
  const postEditModalRef = useRef(null);

  const { updateList, onItemChange, onTagsChanged, removeOnUncollect = false, contentType = 'post', showAuthorInfo = true, showFullContent = false } = props;

  // 狀態碼處理
  const [currentStatusCode, setCurrentStatusCode] = useState('');

  // 優化狀態管理
  const [commentPage, setCommentPage] = useState(1);
  const [expandedSubComments, setExpandedSubComments] = useState({});
  const [showUnlockModal, setShowUnlockModal] = useState(false);
  // post/unlock、subscribe-idol 回應較慢，送出後鎖住確認 modal 並顯示 spinner
  const [isUnlocking, setIsUnlocking] = useState(false);
  const [isSubscribing, setIsSubscribing] = useState(false);
  const [donatedAmount, setDonatedAmount] = useState(null);
  const [showSubscriptionPlanModal, setShowSubscriptionPlanModal] = useState(false);
  const [showSubscriptionConfirmModal, setShowSubscriptionConfirmModal] = useState(false);
  const [selectedSubscriptionPlan, setSelectedSubscriptionPlan] = useState(null);

  // lightGallery 不再需要 startId state，因為它直接通過 props 傳遞

  // 評論參數
  const [commentText, setCommentText] = useState('');
  const [childCommentTexts, setChildCommentTexts] = useState({});
  const [commentId, setCommentId] = useState(null);
  const [childCommentVisibility, setChildCommentVisibility] = useState({});

  //編輯評論
  const [editingComments, setEditingComments] = useState({});
  const [editingSubComments, setEditingSubComments] = useState({});
  const [editingCommentId, setEditingCommentId] = useState(null);

  // 評論輸入框 refs（送出後復原高度用，避免抓到其他卡片或編輯框的 textarea）
  const mainCommentTextareaRef = useRef(null);
  const childCommentTextareaRefs = useRef({});
  // 送出中的輸入框：主留言框為 'main'，回覆框為父留言的 comment_id
  const submittingCommentKeysRef = useRef(new Set());
  // 按讚／收藏送出中的目標：click-like／click-collect 是切換式 API，連點會同時送出多支，
  // 後端不是原子操作，實測會寫出同一帳號讚數 2、3 的髒資料，畫面也和後端相反。回應回來前忽略同一目標的點擊
  const pendingToggleKeysRef = useRef(new Set());
  // 收藏頁取消收藏等待回應中（成功才移除卡片、不做樂觀更新），期間淡化並停用收藏按鈕，讓使用者知道有點到
  const [isUncollectPending, setIsUncollectPending] = useState(false);

  // 取得偶像參數
  const idolId = useSelector((state) => state.user.idolId);
  const avatarUrl = useSelector((state) => state.user.avatarUrl);

  // 優化後的資料處理
  const processedData = useMemo(() => {
    const data = props.data;
    const content = contentType === 'world'
      ? data.world_info.world_content
      : data.post_info.post_content;
    const comments = data.comment_info;

    return {
      post: {
        originalContent: content
      },
      comments: {
        visible: comments.slice(0, commentPage * COMMENTS_PER_PAGE),
        hasMore: comments.length > commentPage * COMMENTS_PER_PAGE,
        remaining: Math.max(0, comments.length - (commentPage * COMMENTS_PER_PAGE)),
        total: comments.length
      }
    };
  }, [props.data, commentPage, contentType]);

  // 判斷是否為擁有者
  const isOwner = useMemo(() => {
    if (contentType === 'world') {
      return idolId === props.data.side_info.idol_id;
    }
    return idolId === props.data.idol_info.idol_id;
  }, [idolId, props.data, contentType]);

  // 顯示用的媒體（CF Stream 影片狀態輪詢會更新它）
  const [oldImages, setOldImages] = useState(() => {
    const data = props.data;
    if (contentType === 'world') {
      return data.world_info.world_media.slice(0, 1);
    }
    return data.post_info.post_media;
  });

  // 刪除
  const deleteContent = async () => {
    const endpoint = contentType === 'world' ? '/world/delete' : '/post/delete';
    const idField = contentType === 'world' ? 'world_id' : 'post_id';
    const id = contentType === 'world'
      ? props.data.world_info.world_id
      : props.data.post_info.post_id;

    try {
      const res = await Axios.post(endpoint, {
        idol_id: idolId,
        [idField]: id,
      });

      if (res.data.status_code === 'SYSTEM_1000') {
        modalDelete.current?.hide();
        modalPostDelSuccess.current?.show();
        if (contentType === 'world') {
          setRedirectTimeout(() => {
            modalPostDelSuccess.current?.hide();
            navigate('/');
          }, 3000);
        } else {
          // 列表頁：onItemChange 本地移除該筆；單篇頁的 patchPost 收到 null 會改走既有 getPost()（404 導頁）
          onItemChange?.(() => null);
          // 刪除會讓該貼文所有 tag 的 usage_count 下降，與編輯一樣要刷新熱門標籤
          onTagsChanged?.();
        }
      } else {
        modalDelete.current?.hide();
        setCurrentStatusCode(res.data.status_code);
        statusModalRef.current?.show();
      }
    } catch (error) {
      // NETWORK_ERROR 由全域 ServerErrorPage 處理，不另外顯示 modal
      if (error.httpErrorCode && error.httpErrorCode !== 'NETWORK_ERROR') {
        modalDelete.current?.hide();
        setCurrentStatusCode(error.httpErrorCode);
        statusModalRef.current?.show();
      } else if (!error.httpErrorCode) {
        console.error('Delete error: ', error);
      }
    }
  };

  // 編輯/解鎖後：重抓該筆最新資料，本地換掉列表中的那一筆。
  // 不沿用 updateList（整包 reset 回第一頁）——無限滾動已載入的頁數會被砍光、
  // 捲動位置錯亂，正是本分支其他互動改成本地更新要解決的問題。
  // 回傳非成功狀態碼交由呼叫端顯示，避免與各自的成功訊息疊成兩個 modal。
  const refetchItem = async () => {
    const uuid = contentType === 'world'
      ? props.data.world_info.world_uuid
      : props.data.post_info.post_uuid;

    try {
      const result = await fetchContentItem({ contentType, uuid, idolId });
      if (result.status_code !== 'SYSTEM_1000') return result.status_code;
      // 淺層合併而非整包取代：混合列表用 item.class 判斷要渲染貼文還是世界觀
      // （CollectionPage、IdolPage 只認 class，沒有其他判斷依據），
      // 而單篇 API 不回傳這個列表專用欄位，直接取代會讓該筆在列表中渲染不出來。
      onItemChange?.((item) => ({ ...item, ...result.data }));
      return null;
    } catch (error) {
      // NETWORK_ERROR 由全域 ServerErrorPage 處理，不另外顯示 modal
      if (error.httpErrorCode && error.httpErrorCode !== 'NETWORK_ERROR') return error.httpErrorCode;
      if (!error.httpErrorCode) console.error('Refetch item error: ', error);
      return null;
    }
  };

  // 編輯儲存後：就地更新該筆，並刷新熱門標籤（貼文 tags 可能被增刪，usage_count 會變）
  const handleEditSaved = async () => {
    const errorCode = await refetchItem();
    if (!errorCode) onTagsChanged?.();
    return errorCode;
  };

  // 點擊 tags
  const updateTagList = useCallback((e, tag) => {
    e.preventDefault();

    // 如果有 onTagClick 回調函數（在首頁），使用它
    if (props.onTagClick) {
      props.onTagClick(tag);
    } else {
      // 否則導向首頁並設定標籤模式
      navigate(`/?tags=${tag}`);
    }
  }, [props, navigate]);

  // 建立評論成功後，用後端回傳的完整留言物件本地插入（一般評論/回覆/打賞留言共用），
  // 不整包刷新，避免列表跳動
  const applyNewComment = (newCommentData, parentId) => {
    const commentWithDefaults = { comment_sub_comments: [], ...newCommentData };
    onItemChange?.((item) => {
      let commentInfo = item.comment_info;
      if (parentId) {
        const { list, changed } = updateCommentTree(item.comment_info, parentId, (parent) => ({
          ...parent,
          comment_sub_comments: [...(parent.comment_sub_comments || []), commentWithDefaults],
        }));
        if (!changed) return item;
        commentInfo = list;
      } else {
        commentInfo = [...item.comment_info, commentWithDefaults];
      }
      // post_comment_count/world_comment_count 頂層留言與回覆都算（開發站實測：新增回覆後端計數 +1）
      if (contentType === 'world') {
        return {
          ...item,
          comment_info: commentInfo,
          world_info: { ...item.world_info, world_comment_count: item.world_info.world_comment_count + 1 },
        };
      }
      return {
        ...item,
        comment_info: commentInfo,
        post_info: { ...item.post_info, post_comment_count: item.post_info.post_comment_count + 1 },
      };
    });
  };

  // 建立評論
  const handleCommentSubmit = async (id = null, text = null) => {
    let content = id ? (text || '') : commentText;

    if (!id && !content.trim()) return;
    if (id && !content.trim()) return;

    // 送出中鎖住同一個輸入框：輸入框要等回應才清空，連按 Enter 會重複送出。
    // 以 ref 同步判斷（state 要等下次 render 才更新，擋不住同一個 tick 內的連按）
    const submitKey = id ?? 'main';
    if (submittingCommentKeysRef.current.has(submitKey)) return;
    submittingCommentKeysRef.current.add(submitKey);

    try {
      const endpoint = contentType === 'world' ? '/world/create-comment' : '/post/create-comment';
      const idField = contentType === 'world' ? 'world_id' : 'post_id';
      const targetId = contentType === 'world'
        ? props.data.world_info.world_id
        : props.data.post_info.post_id;

      const response = await Axios.post(endpoint, {
        idol_id: idolId,
        [idField]: targetId,
        content: content,
        parent_id: id ? id : null
      });

      if (response.data.status_code === 'SYSTEM_1000') {
        if (id) {
          // 子評論：只清空對應的回覆框
          setChildCommentTexts(prev => ({ ...prev, [id]: '' }));
        } else {
          // 一般評論：清空主輸入框並復原高度
          setCommentText('');
          if (mainCommentTextareaRef.current) {
            mainCommentTextareaRef.current.style.height = 'auto';
          }
        }

        applyNewComment(response.data.data, id);
      } else {
        setCurrentStatusCode(response.data.status_code);
        statusModalRef.current?.show();
      }
    } catch (error) {
      // NETWORK_ERROR 由全域 ServerErrorPage 處理，不另外顯示 modal
      if (error.httpErrorCode && error.httpErrorCode !== 'NETWORK_ERROR') {
        setCurrentStatusCode(error.httpErrorCode);
        statusModalRef.current?.show();
      } else if (!error.httpErrorCode) {
        console.error('Create comment error: ', error);
      }
    } finally {
      submittingCommentKeysRef.current.delete(submitKey);
    }
  };

  // 回覆一般評論
  const handleCommentIconClick = useCallback((commentId) => {
    setChildCommentVisibility(prevState => ({
      ...prevState,
      [commentId]: !prevState[commentId]
    }));
  }, []);

  // 送出子評論
  // 不包 useCallback：handleCommentSubmit 每次 render 都會重建，包起來會讀到舊的 commentText
  const handleChildComment = (commentId) => {
    let childText = childCommentTexts[commentId] || '';
    handleCommentSubmit(commentId, childText);
    setChildCommentVisibility(prev => ({ ...prev, [commentId]: false }));

    const textarea = childCommentTextareaRefs.current[commentId];
    if (textarea) {
      textarea.style.height = 'auto';
    }
  };

  const handleKeyDown = (event, commentId = null) => {
    // 組字中按 Enter 是選字，不是送出留言
    if (event.key === 'Enter' && !event.shiftKey && !isImeComposingKey(event)) {
      event.preventDefault();

      if (commentId) {
        handleChildComment(commentId);
      } else {
        handleCommentSubmit(null);
      }
    }
  };

  // 開啟編輯評論框
  const handleEditCommentClick = useCallback((comment, isSubComment = false) => {
    const updateState = isSubComment ? setEditingSubComments : setEditingComments;
    updateState(prev => ({
      ...prev,
      [comment.comment_id]: {
        editing: true,
        content: comment.comment_content
      }
    }));
    setEditingCommentId(comment.comment_id);
  }, []);

  // 取消編輯評論
  const handleCancelEditComment = useCallback((commentId, isSubComment = false) => {
    const updateState = isSubComment ? setEditingSubComments : setEditingComments;
    updateState(prev => {
      const next = { ...prev };
      delete next[commentId];
      return next;
    });
    setEditingCommentId(null);
  }, []);

  // 編輯評論內容
  const handleCommentContentChange = useCallback((commentId, newContent, isSubComment = false) => {
    const updateState = isSubComment ? setEditingSubComments : setEditingComments;
    updateState(prev => ({
      ...prev,
      [commentId]: {
        editing: true,
        content: newContent
      }
    }));
  }, []);

  // 送出評論
  const handleSaveEditedComment = async () => {
    if (!editingCommentId) return;

    const isSubComment = Object.prototype.hasOwnProperty.call(editingSubComments, editingCommentId);
    const editedContent = (isSubComment ? editingSubComments : editingComments)[editingCommentId]?.content;

    try {
      const endpoint = contentType === 'world' ? '/world/update-comment' : '/post/update-comment';
      const response = await Axios.post(endpoint, {
        idol_id: idolId,
        comment_id: editingCommentId,
        content: editedContent
      });

      if (response.data.status_code === 'SYSTEM_1000') {
        modalCommentSent.current?.hide();
        modalCommentSuccess.current?.show();

        const updateState = isSubComment ? setEditingSubComments : setEditingComments;
        updateState(prev => ({
          ...prev,
          [editingCommentId]: {
            editing: false,
            content: editedContent || ''
          }
        }));

        // 非編輯狀態顯示的是 comment_info 裡的 comment_content，本地同步更新該筆內容
        onItemChange?.((item) => {
          const { list, changed } = updateCommentTree(item.comment_info, editingCommentId, (c) => ({
            ...c,
            comment_content: editedContent,
          }));
          return changed ? { ...item, comment_info: list } : item;
        });
      } else {
        modalCommentSent.current?.hide();
        setCurrentStatusCode(response.data.status_code);
        statusModalRef.current?.show();
      }
    } catch (error) {
      // NETWORK_ERROR 由全域 ServerErrorPage 處理，不另外顯示 modal
      if (error.httpErrorCode && error.httpErrorCode !== 'NETWORK_ERROR') {
        modalCommentSent.current?.hide();
        setCurrentStatusCode(error.httpErrorCode);
        statusModalRef.current?.show();
      } else if (!error.httpErrorCode) {
        console.error('Error saving comment: ', error);
      }
    }
  };

  // 刪除評論
  const handleDeleteComment = async () => {
    if (!commentId) return;

    try {
      const endpoint = contentType === 'world' ? '/world/delete-comment' : '/post/delete-comment';
      const idField = contentType === 'world' ? 'world_id' : 'post_id';
      const targetId = contentType === 'world'
        ? props.data.world_info.world_id
        : props.data.post_info.post_id;

      const response = await Axios.post(endpoint, {
        idol_id: idolId,
        [idField]: targetId,
        comment_id: commentId
      });

      if (response.data.status_code === 'SYSTEM_1000') {
        modalDeleteComment.current?.hide();
        modalCommentDelSuccess.current?.show();

        onItemChange?.((item) => {
          const { list, changed } = updateCommentTree(item.comment_info, commentId, () => null);
          if (!changed) return item;
          // 刪除一則就 -1，頂層留言與回覆相同（開發站實測：刪除帶回覆的頂層留言，後端計數也只 -1，
          // 底下的回覆仍算在內；畫面跟著後端，重新整理前後數字一致）
          if (contentType === 'world') {
            return {
              ...item,
              comment_info: list,
              world_info: { ...item.world_info, world_comment_count: item.world_info.world_comment_count - 1 },
            };
          }
          return {
            ...item,
            comment_info: list,
            post_info: { ...item.post_info, post_comment_count: item.post_info.post_comment_count - 1 },
          };
        });
      } else {
        modalDeleteComment.current?.hide();
        setCurrentStatusCode(response.data.status_code);
        statusModalRef.current?.show();
      }
    } catch (error) {
      // NETWORK_ERROR 由全域 ServerErrorPage 處理，不另外顯示 modal
      if (error.httpErrorCode && error.httpErrorCode !== 'NETWORK_ERROR') {
        modalDeleteComment.current?.hide();
        setCurrentStatusCode(error.httpErrorCode);
        statusModalRef.current?.show();
      } else if (!error.httpErrorCode) {
        console.error('Error deleting comment: ', error);
      }
    }
  };

  // Like 喜歡按鈕：樂觀切換本地狀態，不整包 refetch，避免列表跳動；失敗則呼叫同一個 toggle 復原
  const handleLike = async (type, typeId) => {
    const endpoint = contentType === 'world' ? '/world/click-like' : '/post/click-like';
    let typeParam = '';

    if (contentType === 'world') {
      typeParam = type === 'world' ? 'world_id' : 'comment_id';
    } else {
      typeParam = type === 'post' ? 'post_id' : 'comment_id';
    }

    // type 為 comment 時，在（含巢狀回覆的）留言樹裡找到該筆並切換 is_like；
    // 否則切換貼文/世界觀本身的 is_like 與 like_count
    const toggleLike = (item) => {
      if (type === 'comment') {
        const { list, changed } = updateCommentTree(item.comment_info, typeId, (c) => ({
          ...c,
          comment_is_like: !c.comment_is_like,
        }));
        return changed ? { ...item, comment_info: list } : item;
      }
      if (contentType === 'world') {
        const isLiked = item.world_info.world_is_like;
        return {
          ...item,
          world_info: {
            ...item.world_info,
            world_is_like: !isLiked,
            world_like_count: item.world_info.world_like_count + (isLiked ? -1 : 1),
          },
        };
      }
      const isLiked = item.post_info.post_is_like;
      return {
        ...item,
        post_info: {
          ...item.post_info,
          post_is_like: !isLiked,
          post_like_count: item.post_info.post_like_count + (isLiked ? -1 : 1),
        },
      };
    };

    const toggleKey = `like:${type}:${typeId}`;
    if (pendingToggleKeysRef.current.has(toggleKey)) return;
    pendingToggleKeysRef.current.add(toggleKey);

    onItemChange?.(toggleLike);

    try {
      const response = await Axios.post(endpoint, {
        idol_id: idolId,
        [typeParam]: typeId
      });

      if (response.data.status_code !== 'SYSTEM_1000') {
        onItemChange?.(toggleLike); // 失敗復原（toggle 兩次等於還原）
        setCurrentStatusCode(response.data.status_code);
        statusModalRef.current?.show();
      }
    } catch (error) {
      onItemChange?.(toggleLike); // 失敗復原
      if (error.httpErrorCode && error.httpErrorCode !== 'NETWORK_ERROR') {
        setCurrentStatusCode(error.httpErrorCode);
        statusModalRef.current?.show();
      } else if (!error.httpErrorCode) {
        console.error('error: ', error);
      }
    } finally {
      pendingToggleKeysRef.current.delete(toggleKey);
    }
  };

  // Collect 收藏按鈕：樂觀切換本地狀態；removeOnUncollect（收藏頁）情境下取消收藏成功後從列表移除
  const handleCollect = async () => {
    const endpoint = contentType === 'world' ? '/world/click-collect' : '/post/click-collect';
    const idField = contentType === 'world' ? 'world_id' : 'post_id';
    const targetId = contentType === 'world'
      ? props.data.world_info.world_id
      : props.data.post_info.post_id;
    const isCollected = contentType === 'world'
      ? props.data.world_info.world_is_collect
      : props.data.post_info.post_is_collect;
    // 收藏頁取消收藏：等成功才移除，不做樂觀更新。先移除的話，失敗時卡片（連同它的 StatusModal）已經卸載，
    // 錯誤顯示不出來，也無法復原
    const removeOnSuccess = removeOnUncollect && isCollected;

    const toggleCollect = (item) => {
      if (contentType === 'world') {
        return { ...item, world_info: { ...item.world_info, world_is_collect: !item.world_info.world_is_collect } };
      }
      return { ...item, post_info: { ...item.post_info, post_is_collect: !item.post_info.post_is_collect } };
    };

    const toggleKey = 'collect';
    if (pendingToggleKeysRef.current.has(toggleKey)) return;
    pendingToggleKeysRef.current.add(toggleKey);

    if (removeOnSuccess) {
      setIsUncollectPending(true);
    } else {
      onItemChange?.(toggleCollect);
    }

    try {
      const response = await Axios.post(endpoint, {
        idol_id: idolId,
        [idField]: targetId
      });

      if (response.data.status_code === 'SYSTEM_1000') {
        if (removeOnSuccess) {
          onItemChange?.(() => null);
        }
      } else {
        if (!removeOnSuccess) {
          onItemChange?.(toggleCollect); // 失敗復原（toggle 兩次等於還原）
        }
        setCurrentStatusCode(response.data.status_code);
        statusModalRef.current?.show();
      }
    } catch (error) {
      if (!removeOnSuccess) {
        onItemChange?.(toggleCollect); // 失敗復原
      }
      if (error.httpErrorCode && error.httpErrorCode !== 'NETWORK_ERROR') {
        setCurrentStatusCode(error.httpErrorCode);
        statusModalRef.current?.show();
      } else if (!error.httpErrorCode) {
        console.error('error: ', error);
      }
    } finally {
      pendingToggleKeysRef.current.delete(toggleKey);
      if (removeOnSuccess) {
        setIsUncollectPending(false);
      }
    }
  };

  // Share 分享按鈕
  const handleShare = useCallback(async () => {
    const baseUrl = import.meta.env.VITE_APP_DOMAIN;
    let shareUrl = '';

    if (contentType === 'world') {
      shareUrl = `${baseUrl}/world-view/${props.data.world_info.world_uuid}`;
    } else {
      shareUrl = `${baseUrl}/post/${props.data.post_info.post_uuid}`;
    }

    // 寫入剪貼簿可能失敗（瀏覽器拒絕權限；非 https 時 navigator.clipboard 不存在會直接丟 TypeError）：
    // 成功才顯示「已複製」，失敗要告知。writeText 在第一個 await 之前同步呼叫，不會失去點擊的使用者手勢
    try {
      await navigator.clipboard.writeText(shareUrl);
      modalShare.current?.show();
    } catch (error) {
      console.error('Copy share url failed:', error);
      modalShareFailed.current?.show();
    }
  }, [props.data, contentType]);

  // Donate 打賞按鈕
  const handleDonate = useCallback(() => {
    donateModalRef.current?.show();
  }, []);

  const handleDonateConfirm = async (amount, message) => {
    if (!amount || !message.trim()) return;

    try {
      // 打賞只開放貼文（按鈕只在 contentType === 'post' 顯示）
      const response = await Axios.post('/post/create-comment', {
        idol_id: idolId,
        post_id: props.data.post_info.post_id,
        content: message,
        parent_id: null,
        tip_amount: parseFloat(amount)
      });

      if (response.data.status_code === 'SYSTEM_1000') {
        // 用後端回傳的實際扣款金額（後端會四捨五入到兩位），不用使用者輸入值
        setDonatedAmount(response.data.data.comment_tip_amount);
        modalDonateSuccess.current?.show();
        applyNewComment(response.data.data, null);
      } else {
        // 處理非成功的狀態碼
        setCurrentStatusCode(response.data.status_code);
        statusModalRef.current?.show();
      }
    } catch (error) {
      // NETWORK_ERROR 由全域 ServerErrorPage 處理，不另外顯示 modal
      if (error.httpErrorCode && error.httpErrorCode !== 'NETWORK_ERROR') {
        setCurrentStatusCode(error.httpErrorCode);
        statusModalRef.current?.show();
      } else if (!error.httpErrorCode) {
        console.error('Donate error: ', error);
      }
    }
  };


  // 錢包餘額抓不到就關掉扣款 modal 並顯示錯誤碼：餘額未知時不該讓使用者繼續走扣款流程
  const handleUnlockWalletError = useCallback((statusCode) => {
    setShowUnlockModal(false);
    setCurrentStatusCode(statusCode);
    statusModalRef.current?.show();
  }, []);

  const handleSubscribeWalletError = useCallback((statusCode) => {
    setShowSubscriptionConfirmModal(false);
    setCurrentStatusCode(statusCode);
    statusModalRef.current?.show();
  }, []);

  // 解鎖貼文按鈕點擊
  const handleShowUnlockModal = useCallback(() => {
    setShowUnlockModal(true);
  }, []);

  // 確認解鎖貼文。解鎖只影響這一篇（post_is_locked、媒體換成未模糊版），
  // 同樣走單篇重抓而非整包 reset
  const handleUnlockPost = async () => {
    setIsUnlocking(true);
    try {
      const response = await Axios.post('/post/unlock', {
        idol_id: idolId,
        post_id: postData.contentInfo.post_id
      });

      if (response.data.status_code !== 'SYSTEM_1000') {
        setShowUnlockModal(false);
        setCurrentStatusCode(response.data.status_code);
        statusModalRef.current?.show();
        return;
      }

      // 重抓也一併等完再關 modal：spinner 一路持續到內容換成解鎖後的版本，
      // 中途沒有「已關閉但畫面還是鎖著」的空窗，也不會與其他 loading 疊在一起
      const errorCode = await refetchItem();
      setShowUnlockModal(false);
      if (errorCode) {
        setCurrentStatusCode(errorCode);
        statusModalRef.current?.show();
        return;
      }
      modalUnlockSuccess.current?.show();
    } catch (error) {
      setShowUnlockModal(false);
      // NETWORK_ERROR 由全域 ServerErrorPage 處理，不另外顯示 modal
      if (error.httpErrorCode && error.httpErrorCode !== 'NETWORK_ERROR') {
        setCurrentStatusCode(error.httpErrorCode);
        statusModalRef.current?.show();
      } else if (!error.httpErrorCode) {
        console.error('Error unlocking post:', error);
      }
    } finally {
      setIsUnlocking(false);
    }
  };

  // 訂閱解鎖按鈕點擊
  const handleShowSubscribeModal = useCallback(() => {
    setShowSubscriptionPlanModal(true);
  }, []);

  // 選擇訂閱方案
  const handleSubscriptionPlanConfirm = useCallback((plan) => {
    setSelectedSubscriptionPlan(plan);
    setShowSubscriptionPlanModal(false);
    setShowSubscriptionConfirmModal(true);
  }, []);

  // 確認訂閱。訂閱會讓該偶像「所有」訂閱制貼文一起解鎖，不只這一篇，
  // 因此維持整包 updateList
  const handleSubscribeConfirm = async () => {
    setIsSubscribing(true);
    try {
      const response = await Axios.post('/idol/subscribe-idol', {
        idol_id: idolId,
        view_idol_id: postData.authorInfo.idol_id,
        plan_duration: selectedSubscriptionPlan.duration
      });

      setShowSubscriptionConfirmModal(false);
      if (response.data.status_code !== 'SYSTEM_1000') {
        setCurrentStatusCode(response.data.status_code);
        statusModalRef.current?.show();
        return;
      }
      modalSubscribeSuccess.current?.show();
      // updateList 是整包重抓，列表頁自己會顯示 Loading，
      // 因此先關掉確認 modal 再重抓，不會同時出現兩個 loading
      updateList();
    } catch (error) {
      setShowSubscriptionConfirmModal(false);
      // NETWORK_ERROR 由全域 ServerErrorPage 處理，不另外顯示 modal
      if (error.httpErrorCode && error.httpErrorCode !== 'NETWORK_ERROR') {
        setCurrentStatusCode(error.httpErrorCode);
        statusModalRef.current?.show();
      } else if (!error.httpErrorCode) {
        console.error('Error subscribing:', error);
      }
    } finally {
      setIsSubscribing(false);
    }
  };

  // 導向個人頁面
  const goToProfile = useCallback((uuid) => {
    navigate(`/idol/${uuid}`);
  }, [navigate]);

  // 監聽 props.data 變化來同步 oldImages
  useEffect(() => {
    const data = props.data;
    if (contentType === 'world') {
      setOldImages(data.world_info.world_media.slice(0, 1));
    } else {
      setOldImages(data.post_info.post_media);
    }
  }, [props.data, contentType]);

  // 影片狀態輪詢：有未 ready 的影片時，定期批次查詢狀態並更新 oldImages
  const postCreatedAt = contentType === 'post' ? props.data.post_info.post_created_at : null;
  const { videos: unreadyVideos, createdAt: streamCreatedAt } = useMemo(
    () => extractPostUnreadyVideos(contentType === 'post' ? oldImages : [], postCreatedAt),
    [contentType, oldImages, postCreatedAt]
  );
  useStreamPolling({
    endpoint: '/post/get-stream-status',
    unreadyVideos,
    createdAt: streamCreatedAt,
    onStatusChange: (changedItems) => {
      setOldImages(prev => prev.map(m => {
        const update = changedItems.find(v => v.stream_uid === m.stream_uid);
        return update ? { ...m, stream_status: update.stream_status } : m;
      }));
    },
  });

  // 自動調整 textarea 高度
  const autoResizeTextarea = useCallback((e) => {
    const textarea = e.target;
    textarea.style.height = 'auto';
    textarea.style.height = textarea.scrollHeight + 'px';
  }, []);

  // 載入更多評論（留言已全部在手上，只是多顯示一頁）
  const loadMoreComments = useCallback(() => {
    setCommentPage(prev => prev + 1);
  }, []);

  // 切換子評論展開狀態
  const toggleSubComments = useCallback((commentId) => {
    setExpandedSubComments(prev => ({
      ...prev,
      [commentId]: !prev[commentId]
    }));
  }, []);

  // 渲染內容
  const renderContent = () => {
    const { post } = processedData;
    const content = post.originalContent;

    if (!content) return null;

    // Tags 處理
    const data = props.data;
    const tags = contentType === 'world'
      ? data.world_info.world_tags
      : data.post_info.post_tags;

    // tags 僅在內容完整顯示（展開、或本來就沒被截斷）時出現
    const renderTags = ({ expanded, overflowing }) => {
      if (!(expanded || !overflowing) || tags.length === 0) return null;
      return (
        <div className="mt-1">
          {tags.map((tag) => (
            <span key={tag.tag_id}>
              <a href="#" onClick={(e) => updateTagList(e, tag.tag_id)}>
                #{tag.tag_name}
              </a>
              <span> </span>
            </span>
          ))}
        </div>
      );
    };

    return (
      <div>
        <ClampText
          text={content}
          clampClassName="post-content-container"
          forceFull={showFullContent}
          beforeToggle={renderTags}
        />
      </div>
    );
  };

  // 取得資料
  const getPostData = () => {
    if (contentType === 'world') {
      const data = props.data;
      // 所有世界觀來源（標籤、收藏、搜尋、/world/get-info）都是 side_info + world_info 結構
      const worldInfo = data.world_info;
      return {
        authorInfo: data.side_info,
        contentInfo: worldInfo,
        createdAt: worldInfo.world_created_at,
        likeCount: worldInfo.world_like_count,
        commentCount: worldInfo.world_comment_count,
        isLike: worldInfo.world_is_like,
        isCollect: worldInfo.world_is_collect,
        title: worldInfo.world_title
      };
    } else {
      const data = props.data;
      return {
        authorInfo: data.idol_info,
        contentInfo: data.post_info,
        createdAt: data.post_info.post_created_at,
        likeCount: data.post_info.post_like_count,
        commentCount: data.post_info.post_comment_count,
        isLike: data.post_info.post_is_like,
        isCollect: data.post_info.post_is_collect,
        title: null,
        visibilityType: data.post_info.post_visibility_type,
        isLocked: data.post_info.post_is_locked,
        lockType: data.post_info.post_lock_type,
        unlockPrice: data.post_info.post_unlock_price,
        isScheduled: data.post_info.post_is_scheduled
      };
    }
  };

  const postData = getPostData();

  // 非自己的貼文且已可觀看：付費(2) / 隱藏付費(4) 顯示「已解鎖」，訂閱專屬(1) 顯示「訂閱專屬」
  const isViewerVisiblePost = contentType === 'post'
    && postData.authorInfo.idol_id !== idolId
    && postData.isLocked === false;
  const isUnlockedPost = isViewerVisiblePost && [2, 4].includes(postData.visibilityType);
  const isSubscriberPost = isViewerVisiblePost && postData.visibilityType === 1;

  // 自己的貼文：瀏覽權限與排程狀態交給共用的 PostSettingBadges（與建立/編輯貼文同一組標籤）
  const isOwnPost = contentType === 'post' && isOwner;
  // 仍在排程中的貼文，post_created_at 是預定發布時間（後端 UTC 字串）
  const scheduledAtMs = isOwnPost && postData.isScheduled ? parseBackendDate(postData.createdAt) : null;
  const scheduledAt = scheduledAtMs ? new Date(scheduledAtMs) : null;

  return (
    <>
      <div className="activity-item mb-3 rounded bg-white shadowA border">
        <div className="p-3">
          {/* 頭部區域 */}
          {showAuthorInfo && (
            <div className="item-top-bar d-flex align-items-start">
            <a href={`/idol/${postData.authorInfo.idol_uuid}`} className="link-none hover-p d-block flex-grow-1 min-w-0">
              <div className="left-bar position-relative mb-2 d-flex">
                <IdolAvatar
                  size="sm"
                  src={postData.authorInfo.idol_avatar_url || placeholder_portrait_40x40}
                  idolId={postData.authorInfo.idol_id}
                  className="me-2"
                  alt={postData.authorInfo.idol_nick_name}
                />
                <div className="activity-idol-info min-w-0" onClick={() => goToProfile(postData.authorInfo.idol_uuid)}>
                  <div className="all-name min-w-0">
                    <IdolName
                      name={postData.authorInfo.idol_nick_name}
                      isOfficial={postData.authorInfo.idol_is_official}
                      className="name"
                    />
                  </div>
                  <div className="time">
                    <RelativeTime value={postData.createdAt} />
                  </div>
                </div>
              </div>
            </a>

            <div className="d-flex align-items-center flex-shrink-0 ms-2 gap-1">
              {/* Badge 顯示內容類型 */}
              {props.showBadge && (
                <span className={`badge rounded-pill ${contentType === 'world' ? 'text-bg-warning' : 'text-bg-blue'}`}>
                  {t(contentType === 'world' ? 'world' : 'post')}
                </span>
              )}

              {isOwner && (
                <Dropdown>
                  <Dropdown.Toggle as={EllipsisToggle} iconClassName="fs-14 link-gray" />
                  <Dropdown.Menu align="end">
                    <Dropdown.Item as="button" onClick={() => postEditModalRef.current?.show(props.data)}>
                      <FontAwesomeIcon icon={faPen} className="me-2" />
                      {t('edit_post')}
                    </Dropdown.Item>
                    <Dropdown.Item as="button" onClick={() => modalDelete.current?.show()}>
                      <FontAwesomeIcon icon={faTrash} className="me-2" />
                      {t('delete_post')}
                    </Dropdown.Item>
                  </Dropdown.Menu>
                </Dropdown>
              )}

              {!isOwner && contentType === 'post' && !postData.isLocked && (
                <Dropdown>
                  <Dropdown.Toggle as={EllipsisToggle} iconClassName="fs-14 link-gray" />
                  <Dropdown.Menu align="end">
                    <Dropdown.Item
                      as="button"
                      onClick={() => reportModalRef.current?.show(props.data.post_info.post_id)}
                    >
                      <FontAwesomeIcon icon={faFlag} className="me-2" />
                      {t('report', { target: t('post') })}
                    </Dropdown.Item>
                  </Dropdown.Menu>
                </Dropdown>
              )}
            </div>
          </div>
          )}

          {/* 標籤列（內容上方整行、靠右）：作者看自己的權限／排程設定，
              他人看已解鎖／訂閱專屬狀態，兩者互斥（自己的貼文不會有 isUnlockedPost / isSubscriberPost）。
              放在作者列右上角會擠壓暱稱與時間，長標籤也塞不下 */}
          {isOwnPost ? (
            <PostSettingBadges
              visibilityType={postData.visibilityType}
              unlockPrice={postData.unlockPrice}
              scheduledAt={scheduledAt}
              className="justify-content-end mb-2"
            />
          ) : (isUnlockedPost || isSubscriberPost) && (
            <div className="d-flex flex-wrap justify-content-end gap-1 mb-1">
              {isUnlockedPost && (
                <span className="badge rounded-pill tip-gradient text-dark fw-bold">
                  <FontAwesomeIcon icon={faLockOpen} className="me-1" />
                  {t('unlocked')}
                </span>
              )}
              {isSubscriberPost && (
                <span className="badge rounded-pill tip-gradient text-dark fw-bold">
                  <FontAwesomeIcon icon={faGem} className="me-1" />
                  {t('post_visibility_subscribers')}
                </span>
              )}
            </div>
          )}

          {/* 內容區域 */}
          <div className="main-content position-relative">

          {/* 世界觀標題 */}
          {contentType === 'world' && postData.title && (
            <>
              {props.linkToDetail ? (
                <a href={`/world-view/${postData.contentInfo.world_uuid}`}
                  className="h4 link-none hover-p">
                  {postData.title}
                </a>
              ) : (
                <h4>{postData.title}</h4>
              )}
            </>
          )}

          {/* 世界觀圖片顯示在內容前 */}
          {contentType === 'world' && (
            <PostImages
              contentType="world"
              oldImages={oldImages}
              newImages={[]}
              editing={false}
            />
          )}

          <div className="post-edit">
            {renderContent()}
          </div>

          {/* 貼文圖片顯示在內容後 */}
          {contentType === 'post' && (
            <PostImages
              contentType="post"
              oldImages={oldImages}
              newImages={[]}
              editing={false}
              isLocked={postData.isLocked}
              lockType={postData.lockType}
              unlockPrice={postData.unlockPrice}
              onUnlockClick={handleShowUnlockModal}
              onSubscribeClick={handleShowSubscribeModal}
            />
          )}
          </div>

          {/* 統計資訊 */}
          <div className="d-flex justify-content-end mt-3 gap-2">
            <div className="text-gray me-2">
              <span className="me-1">{postData.likeCount}</span>
              <FontAwesomeIcon icon={faThumbsUp} />
            </div>
            <div className="text-gray">
              <span className="me-1">{postData.commentCount}</span>
              <FontAwesomeIcon icon={faComment} />
            </div>
          </div>
        </div>

        {/* 操作區域 - 鎖定貼文不顯示 */}
        {!(contentType === 'post' && postData.isLocked) && (
          <div className="item-btm-bar">
            <div className="border-top border-bottom  row justify-content-between mx-0">
              <div className="col-3 py-2 text-center text-gray hover-p"
                onClick={() => handleLike(contentType === 'world' ? 'world' : 'post', contentType === 'world' ? postData.contentInfo.world_id : postData.contentInfo.post_id)}>
                {postData.isLike ? (
                  <FontAwesomeIcon icon={faThumbsUp} className="text-primary me-1" />
                ) : (
                  <FontAwesomeIcon icon={faThumbsUpRegular} className="me-1" />
                )}
                <span>{t('like')}</span>
              </div>
              <div className={`col-3 py-2 text-center text-gray hover-p ${isUncollectPending ? 'opacity-50 pe-none' : ''}`} onClick={handleCollect}>
                {postData.isCollect ? (
                  <FontAwesomeIcon icon={faHeart} className="text-primary me-1" />
                ) : (
                  <FontAwesomeIcon icon={faHeartRegular} className="me-1" />
                )}
                <span>{t('collect')}</span>
              </div>
              <div className="col-3 py-2 text-center text-gray hover-p" onClick={handleShare}>
                <FontAwesomeIcon icon={faShare} className="me-1" />
                <span>{t('share')}</span>
              </div>
              {/* 世界觀留言不支援打賞（後端會忽略 tip_amount，建立成一般留言且不扣款），只有貼文顯示 */}
              {contentType === 'post' && postData.authorInfo.idol_id !== idolId && (
                <div className="col-3 py-2 text-center text-gray hover-p" onClick={handleDonate}>
                  <FontAwesomeIcon icon={faDonate} className="me-1" />
                  <span>{t('donate')}</span>
                </div>
              )}
            </div>
          </div>
        )}

        {/* 評論區域 - 鎖定貼文不顯示 */}
        {!(contentType === 'post' && postData.isLocked) && (
          <PostComments
          processedCommentsData={processedData.comments}
          commentState={{
            commentText,
            childCommentTexts,
            childCommentVisibility,
            expandedSubComments,
            editingComments,
            editingSubComments
          }}
          commentHandlers={{
            setCommentText,
            setChildCommentTexts,
            onCommentSubmit: handleCommentSubmit,
            onCommentIconClick: handleCommentIconClick,
            onChildComment: handleChildComment,
            onKeyDown: handleKeyDown,
            onEditCommentClick: handleEditCommentClick,
            onCancelEditComment: handleCancelEditComment,
            onCommentContentChange: handleCommentContentChange,
            // 送出時才指定要存哪一則：同時開著多個編輯框時，editingCommentId 原本是最後點「編輯」的那一則，會存錯
            onRequestSaveEdit: (commentId) => {
              setEditingCommentId(commentId);
              modalCommentSent.current?.show();
            },
            onDeleteComment: (commentId) => {
              setCommentId(commentId);
              modalDeleteComment.current?.show();
            },
            onLike: handleLike,
            onLoadMoreComments: loadMoreComments,
            onToggleSubComments: toggleSubComments,
            onAutoResizeTextarea: autoResizeTextarea
          }}
          commentInputRefs={{
            mainCommentTextareaRef,
            childCommentTextareaRefs
          }}
          userInfo={{
            avatarUrl,
            idolId,
            isOwner
          }}
          />
        )}
      </div>

      {/* Modals */}
      <MessageModal ref={modalDelete} status="warning" size="md">
        <h5 className="mb-5">{contentType === 'world' ? t('world_delete_confirm') : t('post_warning1')}</h5>
        <div className="text-end">
          <button type="button" className="btn btn-neutral rounded-pill me-3" onClick={() => modalDelete.current?.hide()}>{t('cancel')}</button>
          <button type="button" className="btn btn-primary rounded-pill" onClick={deleteContent}>{t('confirm')}</button>
        </div>
      </MessageModal>

      <MessageModal ref={modalDeleteComment} status="warning" size="md">
        <h5 className="mb-5">{t('comment_delete_confirm')}</h5>
        <div className="text-end">
          <button type="button" className="btn btn-neutral rounded-pill me-3" onClick={() => modalDeleteComment.current?.hide()}>{t('cancel')}</button>
          <button type="button" className="btn btn-primary rounded-pill" onClick={handleDeleteComment}>{t('confirm')}</button>
        </div>
      </MessageModal>

      <MessageModal ref={modalCommentSent} status="warning" size="md">
        <h5 className="mb-5">{t('save_confirm')}</h5>
        <div className="text-end">
          <button type="button" className="btn btn-neutral rounded-pill me-3" onClick={() => modalCommentSent.current?.hide()}>{t('cancel')}</button>
          <button type="button" className="btn btn-primary rounded-pill" onClick={handleSaveEditedComment}>{t('confirm')}</button>
        </div>
      </MessageModal>

      <MessageModal ref={modalShare} status="success">
        <p>{contentType === 'world' ? t('world_url_copied') : t('post_success1')}</p>
      </MessageModal>

      <MessageModal ref={modalShareFailed} status="error">
        <p>{t('copy_failed')}</p>
      </MessageModal>

      <MessageModal ref={modalPostDelSuccess} status="success">
        <p>{contentType === 'world' ? t('world_deleted') : t('post_success3')}</p>
      </MessageModal>

      <MessageModal ref={modalCommentSuccess} status="success">
        <p>{t('comment_saved')}</p>
      </MessageModal>

      <MessageModal ref={modalCommentDelSuccess} status="success">
        <p>{t('comment_deleted')}</p>
      </MessageModal>

      <PostEditModal
        ref={postEditModalRef}
        contentType={contentType}
        onSaved={handleEditSaved}
      />

      {/* 解鎖貼文確認 Modal */}
      <PostUnlockModal
        show={showUnlockModal}
        onHide={() => setShowUnlockModal(false)}
        onConfirm={handleUnlockPost}
        onError={handleUnlockWalletError}
        creatorName={postData.authorInfo.idol_nick_name}
        unlockPrice={postData.unlockPrice}
        isLoading={isUnlocking}
      />

      {/* 解鎖成功 Modal */}
      <MessageModal ref={modalUnlockSuccess} status="success">
        <p>{t('unlock_success')}</p>
      </MessageModal>

      {/* 訂閱方案選擇 Modal */}
      <SubscriptionPlanModal
        show={showSubscriptionPlanModal}
        onHide={() => setShowSubscriptionPlanModal(false)}
        onConfirm={handleSubscriptionPlanConfirm}
        mode="subscribe"
        idolId={postData.authorInfo.idol_id}
      />

      {/* 訂閱確認 Modal */}
      <SubscriptionConfirmModal
        show={showSubscriptionConfirmModal}
        onHide={() => setShowSubscriptionConfirmModal(false)}
        onConfirm={handleSubscribeConfirm}
        onError={handleSubscribeWalletError}
        mode="subscribe"
        idolName={postData.authorInfo.idol_nick_name}
        selectedPlan={selectedSubscriptionPlan}
        isLoading={isSubscribing}
      />

      {/* 訂閱成功 Modal */}
      <MessageModal ref={modalSubscribeSuccess} status="success">
        <p>{t('subscription_success')}</p>
      </MessageModal>

      {/* 打賞 Modal */}
      <DonateModal
        ref={donateModalRef}
        creatorName={postData.authorInfo.idol_nick_name}
        onConfirm={handleDonateConfirm}
      />

      {/* 打賞成功 Modal */}
      <MessageModal ref={modalDonateSuccess} status="success" customIcon={faDonate}>
        <p className="mb-1">{t('donate_success_message')}</p>
        <p className="mb-1 fw-bold break-anywhere">{postData.authorInfo.idol_nick_name}</p>
        {donatedAmount && <p className="mb-0 text-primary fs-18 fw-bold">₣{donatedAmount} FCoin</p>}
      </MessageModal>

      {/* 舉報貼文 Modal */}
      <ReportModal ref={reportModalRef} targetKey="post" />

      {/* 狀態碼 Modal */}
      <StatusModal ref={statusModalRef} statusCode={currentStatusCode} />

    </>
  );
};

PostCard.propTypes = {
  // 貼文：{ post_info, idol_info, comment_info }；世界觀：{ world_info, side_info, comment_info }
  data: PropTypes.object.isRequired,
  contentType: PropTypes.oneOf(['post', 'world']),
  updateList: PropTypes.func.isRequired,
  onItemChange: PropTypes.func,
  onTagsChanged: PropTypes.func,
  onTagClick: PropTypes.func,
  removeOnUncollect: PropTypes.bool,
  showAuthorInfo: PropTypes.bool,
  showFullContent: PropTypes.bool,
  showBadge: PropTypes.bool,
  linkToDetail: PropTypes.bool,
};

export default PostCard;
